import { bech32 } from "bech32";
import { secp256k1 } from "@noble/curves/secp256k1.js";
import { keccak_256 } from "@noble/hashes/sha3.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { BinaryWriter } from "cosmjs-types/binary.js";
import { Any } from "cosmjs-types/google/protobuf/any.js";
import {
  AuthInfo,
  Fee,
  ModeInfo,
  SignDoc,
  SignerInfo,
  TxBody,
} from "cosmjs-types/cosmos/tx/v1beta1/tx.js";
import { SignMode } from "cosmjs-types/cosmos/tx/signing/v1beta1/signing.js";
import { CHAIN } from "./config.mjs";

const textEncoder = new TextEncoder();

export function fromBase64(value) {
  if (typeof Buffer !== "undefined") return Uint8Array.from(Buffer.from(value, "base64"));
  const binary = atob(value);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

export function toBase64(bytes) {
  if (typeof Buffer !== "undefined") return Buffer.from(bytes).toString("base64");
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function hex(bytes) {
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function encodeEthPubkey(key) {
  if (!(key instanceof Uint8Array) || key.length !== 33) {
    throw new Error("Expected a 33-byte compressed ethsecp256k1 public key");
  }
  return BinaryWriter.create().uint32(10).bytes(key).finish();
}

export function encodeCheckoutItem(item, writer = BinaryWriter.create()) {
  writer.uint32(10).string(item.product_id);
  writer.uint32(18).string(item.tag_uid);
  writer.uint32(24).uint32(item.quantity);
  writer.uint32(34).string(item.unit_price);
  writer.uint32(42).string(item.line_total);
  writer.uint32(50).string(item.expected_owner);
  return writer;
}

export function encodeMsgExecuteCheckoutV2(message, writer = BinaryWriter.create()) {
  writer.uint32(10).string(message.buyer);
  writer.uint32(18).string(message.merchant_address);
  writer.uint32(26).string(message.merchant_id);
  writer.uint32(34).string(message.terminal_id);
  writer.uint32(42).string(message.terminal_key_id);
  writer.uint32(50).string(message.chain_id);
  writer.uint32(58).string(message.checkout_id);
  writer.uint32(66).string(message.request_id);
  writer.uint32(74).string(message.request_hash);
  writer.uint32(80).int64(BigInt(message.created_at_ms));
  writer.uint32(88).int64(BigInt(message.expires_at_ms));
  writer.uint32(98).string(message.denomination);
  writer.uint32(106).string(message.total_amount);
  for (const product of message.products) {
    encodeCheckoutItem(product, writer.uint32(114).fork()).ldelim();
  }
  writer.uint32(122).string(message.nonce);
  writer.uint32(130).string(message.ipi_card_commitment);
  writer.uint32(138).string(message.card_verification_config_id);
  writer.uint32(146).string(message.card_proof_identity);
  writer.uint32(152).uint64(BigInt(message.card_read_counter));
  writer.uint32(162).string(message.terminal_signature);
  return writer;
}

export function makeCheckoutSignDoc(fixture) {
  if (fixture.message_type_url !== CHAIN.checkoutTypeUrl) {
    throw new Error("Fixture does not contain the real IPI checkout V2 type URL");
  }
  const messageBytes = encodeMsgExecuteCheckoutV2(fixture.message).finish();
  const bodyBytes = TxBody.encode(TxBody.fromPartial({
    messages: [Any.fromPartial({
      typeUrl: fixture.message_type_url,
      value: messageBytes,
    })],
    memo: fixture.memo,
  })).finish();
  const publicKey = Any.fromPartial({
    typeUrl: fixture.public_key_type_url,
    value: encodeEthPubkey(fromBase64(fixture.signer_public_key_base64)),
  });
  const authInfoBytes = AuthInfo.encode(AuthInfo.fromPartial({
    signerInfos: [SignerInfo.fromPartial({
      publicKey,
      modeInfo: ModeInfo.fromPartial({
        single: { mode: SignMode.SIGN_MODE_DIRECT },
      }),
      sequence: BigInt(fixture.sequence),
    })],
    fee: Fee.fromPartial({
      amount: [{ denom: fixture.fee.denom, amount: fixture.fee.amount }],
      gasLimit: BigInt(fixture.fee.gas_limit),
    }),
  })).finish();
  const signDoc = SignDoc.fromPartial({
    bodyBytes,
    authInfoBytes,
    chainId: fixture.chain_id,
    accountNumber: BigInt(fixture.account_number),
  });
  return { messageBytes, bodyBytes, authInfoBytes, signDoc };
}

export function signDocBytes(signDoc) {
  return SignDoc.encode(signDoc).finish();
}

function sameBytes(left, right) {
  return left.length === right.length
    && left.every((byte, index) => byte === right[index]);
}

export function assertUnchangedSignDoc(expected, returned) {
  if (
    returned.chainId !== expected.chainId
    || BigInt(returned.accountNumber) !== BigInt(expected.accountNumber)
    || !sameBytes(returned.bodyBytes, expected.bodyBytes)
    || !sameBytes(returned.authInfoBytes, expected.authInfoBytes)
  ) {
    throw new Error("Keplr materially changed the checkout SignDoc");
  }
}

export function addressFromEthPubkey(compressedPublicKey) {
  const point = secp256k1.Point.fromBytes(compressedPublicKey);
  const uncompressed = point.toBytes(false);
  const addressBytes = keccak_256(uncompressed.slice(1)).slice(-20);
  return bech32.encode(CHAIN.addressPrefix, bech32.toWords(addressBytes));
}

export function assertFixtureSigner(fixture, key, account) {
  if (!key || !(key.pubKey instanceof Uint8Array) || key.pubKey.length !== 33) {
    throw new Error("Keplr did not return a compressed ethsecp256k1 public key");
  }
  const expectedKey = fromBase64(fixture.signer_public_key_base64);
  if (
    key.bech32Address !== fixture.signer_address
    || addressFromEthPubkey(key.pubKey) !== fixture.signer_address
    || !sameBytes(key.pubKey, expectedKey)
  ) {
    throw new Error("Connected Keplr signer does not match the V3-03 fixture");
  }
  if (
    BigInt(account.accountNumber) !== BigInt(fixture.account_number)
    || BigInt(account.sequence) !== BigInt(fixture.sequence)
  ) {
    throw new Error("Live account number or sequence does not match the deterministic fixture");
  }
}

export function verifyCompactSignature(signDoc, signatureBase64, publicKeyBase64) {
  const signature = fromBase64(signatureBase64);
  if (signature.length !== 64) {
    throw new Error(`Keplr returned a ${signature.length}-byte signature; expected compact 64-byte`);
  }
  const publicKey = fromBase64(publicKeyBase64);
  const digest = keccak_256(signDocBytes(signDoc));
  return secp256k1.verify(signature, digest, publicKey, { prehash: false });
}

export function fixtureHashes(fixture) {
  const encoded = makeCheckoutSignDoc(fixture);
  return {
    message_bytes_sha256: hex(sha256(encoded.messageBytes)),
    body_bytes_sha256: hex(sha256(encoded.bodyBytes)),
    auth_info_bytes_sha256: hex(sha256(encoded.authInfoBytes)),
    sign_doc_bytes_sha256: hex(sha256(signDocBytes(encoded.signDoc))),
  };
}

export function validateFixture(fixture) {
  if (
    fixture.fixture_version !== "V3-03-1"
    || fixture.chain_id !== CHAIN.chainId
    || fixture.message.chain_id !== CHAIN.chainId
    || fixture.message.buyer !== fixture.signer_address
    || fixture.signing_mode !== CHAIN.signingMode
    || fixture.public_key_type_url !== CHAIN.pubkeyTypeUrl
    || fixture.message_type_url !== CHAIN.checkoutTypeUrl
    || fixture.message_type_url.includes("placeholder")
  ) {
    throw new Error("Invalid or placeholder checkout V2 fixture");
  }
  if (addressFromEthPubkey(fromBase64(fixture.signer_public_key_base64))
      !== fixture.signer_address) {
    throw new Error("Fixture signer public key does not derive its buyer address");
  }
  const actual = fixtureHashes(fixture);
  for (const [name, value] of Object.entries(actual)) {
    if (fixture[name] !== value) {
      throw new Error(`Fixture ${name} mismatch`);
    }
  }
  return makeCheckoutSignDoc(fixture);
}

export function checkoutDisplayFields(fixture) {
  const message = fixture.message;
  return Object.freeze({
    typeUrl: fixture.message_type_url,
    chainId: message.chain_id,
    buyer: message.buyer,
    merchant: message.merchant_address,
    merchantId: message.merchant_id,
    terminalId: message.terminal_id,
    terminalKeyId: message.terminal_key_id,
    checkoutId: message.checkout_id,
    requestId: message.request_id,
    requestHash: message.request_hash,
    createdAtMs: String(message.created_at_ms),
    expiresAtMs: String(message.expires_at_ms),
    denomination: message.denomination,
    totalAmount: message.total_amount,
    productId: message.products[0].product_id,
    tagUid: message.products[0].tag_uid,
    quantity: String(message.products[0].quantity),
    unitPrice: message.products[0].unit_price,
    expectedOwner: message.products[0].expected_owner,
    cardCommitment: message.ipi_card_commitment,
    cardConfig: message.card_verification_config_id,
    cardProof: message.card_proof_identity,
    cardCounter: String(message.card_read_counter),
    terminalSignature: message.terminal_signature,
  });
}

export function mutateFixturePrice(fixture) {
  const mutated = structuredClone(fixture);
  mutated.message.products[0].unit_price = "1";
  mutated.message.products[0].line_total = "1";
  mutated.message.total_amount = "1";
  return mutated;
}

export function utf8(value) {
  return textEncoder.encode(value);
}
